import { useMemo } from 'react'

import { SkeletonBox, DescriptionList } from '@circlefin/components'
import { useDate } from '@circlefin/hooks'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { useFactorQuery, FactorType } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

export interface ActiveFactorProps {
  /**
   * Factor Type.
   */
  factorType: FactorType
  /**
   * Children.
   */
  children?: React.ReactNode
}

export const ActiveFactor: React.FC<ActiveFactorProps> = ({
  factorType,
  children,
}) => {
  const { t } = useTranslation('settings')
  const { date } = useDate()
  const { data, loading, error, refetch } = useFactorQuery({
    variables: { factorType },
  })

  const translationKey = useMemo(() => {
    switch (factorType) {
      case FactorType.PUSH:
        return 'oktaVerify'
      case FactorType.PIN_CODE:
        return 'pinCode'
      case FactorType.TOKEN_SOFTWARE_TOTP:
        return 'authenticatorApp'
    }
  }, [factorType])

  // If the factor does not exist for user, we shouldn't render anything
  if (!loading && !error && !data?.factor) {
    return null
  }

  return (
    <>
      <DescriptionList.Label
        className="w-40"
        data-testid={`login-security-info-${translationKey}`}
      >
        <SkeletonBox className="h-6 w-36" loading={loading}>
          {t(`security.loginAndSecurity.${translationKey}.label`)}
        </SkeletonBox>
      </DescriptionList.Label>

      <DescriptionList.Description className="flex flex-row justify-between">
        <GraphQLErrorBoundary error={error} retry={refetch} variant="component">
          <SkeletonBox className="h-12 w-full" loading={loading}>
            <div className="flex flex-col">
              <span>
                {t(`security.loginAndSecurity.${translationKey}.description`)}
              </span>

              {data?.factor?.dateCreated && (
                <p className="text-neutral-subtle">
                  {t(
                    `security.loginAndSecurity.${translationKey}.lastUpdated`,
                    {
                      date: date(data.factor.dateCreated),
                    },
                  )}
                </p>
              )}
            </div>
          </SkeletonBox>

          {!loading && <>{children}</>}
        </GraphQLErrorBoundary>
      </DescriptionList.Description>
    </>
  )
}
